import JsBarcode from 'jsbarcode';

/**
 * @file ./models/utils/codigoBarrasDanfe.js
 *
 * Geração do código de barras da chave de acesso do DANFE conforme MOC 7.0
 * (Anexo II, item 3). Requisitos normativos implementados aqui:
 *
 *   - Simbologia **CODE-128C** (não o CODE-128 automático): o subconjunto C
 *     comprime dois dígitos por símbolo, e é o único aceito para a chave.
 *   - **Margem clara (quiet zone)** obrigatória antes do *Start* e depois do
 *     *Stop*. O mínimo da especificação Code 128 é 10 módulos de cada lado.
 *   - Altura mínima de 0,8 cm e largura mínima de 6 cm da área impressa —
 *     essas duas são responsabilidade do layout (que dimensiona a caixa da
 *     imagem); aqui garantimos apenas que a proporção gerada não distorça as
 *     barras.
 *
 * A imagem é gerada num `<canvas>` off-screen com resolução bem acima da de
 * impressão (`MODULO_PX` px por módulo) para que o reescalonamento feito
 * pelo PDF até a largura física final não borre as transições de barra —
 * borrão em transição é a causa mais comum de falha de leitura óptica.
 *
 * Depende de `document`/`canvas`: só roda em ambiente de browser. Layouts
 * recebem esta função por injeção (`opcoes.gerarCodigoBarras`) justamente
 * para que uma futura renderização server-side possa trocar a implementação
 * sem tocar no template.
 */

/** Resolução da geração: pixels de canvas por módulo do Code 128. */
const MODULO_PX = 6;

/** Margem clara mínima da especificação Code 128, em módulos. */
const QUIET_ZONE_MODULOS = 10;

/** Altura da imagem gerada, em pixels de canvas. */
const ALTURA_PX = 160;

/**
 * Número de módulos ocupados pelo símbolo CODE-128C de `n` dígitos,
 * desconsiderando a margem clara.
 *
 * Composição do Code 128: Start (11 módulos) + N/2 símbolos de dados
 * (11 cada) + dígito de verificação (11) + Stop (13, inclui a barra final
 * dupla). Serve para o layout calcular a largura física exata e conferir o
 * mínimo de 6 cm.
 *
 * @param {number} quantidadeDigitos - Quantidade de dígitos codificados (par).
 * @returns {number} Total de módulos do símbolo.
 */
export function modulosCode128C(quantidadeDigitos) {
  const simbolosDeDados = Math.ceil(quantidadeDigitos / 2);
  return 11 + simbolosDeDados * 11 + 11 + 13;
}

/**
 * Gera o código de barras CODE-128C da chave de acesso.
 *
 * @param {string} chaveAcesso - Chave de acesso da NF-e (44 dígitos numéricos).
 * @returns {{ src: string, modulos: number, proporcao: number }}
 *   `src` é o data URI PNG; `modulos` é a largura total em módulos incluindo
 *   as duas margens claras; `proporcao` é largura/altura da imagem gerada.
 * @throws {Error} Se a chave não tiver exatamente 44 dígitos numéricos.
 */
export function gerarCodigoBarrasDanfe(chaveAcesso) {
  const chave = String(chaveAcesso ?? '').replace(/\D/g, '');

  if (chave.length !== 44) {
    throw new Error(
      `[gerarCodigoBarrasDanfe] Chave de acesso inválida: esperado 44 dígitos numéricos, recebido ${chave.length}.`,
    );
  }

  const canvas = document.createElement('canvas');

  JsBarcode(canvas, chave, {
    format: 'CODE128C',
    displayValue: false, // A chave é impressa pelo layout, em negrito, em 11 blocos de 4.
    margin: 0,
    marginLeft: QUIET_ZONE_MODULOS * MODULO_PX,
    marginRight: QUIET_ZONE_MODULOS * MODULO_PX,
    width: MODULO_PX,
    height: ALTURA_PX,
    background: '#FFFFFF',
    lineColor: '#000000',
  });

  const modulos = modulosCode128C(chave.length) + QUIET_ZONE_MODULOS * 2;

  return {
    src: canvas.toDataURL('image/png'),
    modulos,
    proporcao: canvas.width / canvas.height,
  };
}
